import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, CreditCard, FileText, Loader2, ShieldAlert } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { api, errMsg, money } from "@/lib/api";

export default function OrderFinish() {
  const { token = "" } = useParams();
  const [info, setInfo] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [inv, setInv] = useState(false);
  const [addr, setAddr] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => { api.get(`/public/order-finish/${token}`).then((r) => { setInfo(r.data); setAddr(r.data.address || ""); }).catch((e) => setErr(errMsg(e))); }, [token]);
  const card = async () => {
    setBusy("card"); setErr("");
    try { window.location.href = (await api.post(`/public/order-finish/${token}/card`, { origin_url: window.location.origin })).data.checkout_url; return; } catch (e) { setErr(errMsg(e)); }
    setBusy("");
  };
  const invoice = async () => {
    setBusy("inv"); setErr("");
    try { await api.post(`/public/order-finish/${token}/invoice`, { address: addr }); setDone(true); } catch (e) { setErr(errMsg(e)); }
    setBusy("");
  };
  return (
    <PublicShell>
      <div className="mx-auto max-w-xl px-4 py-16">
        <div className="card-soft fade-up p-8 sm:p-10" data-testid="order-finish-card">
          <h1 className="text-3xl font-extrabold tracking-tight">Finish your order</h1>
          {!info && !err && <Loader2 className="mt-6 h-6 w-6 animate-spin text-[#94A3B8]" />}
          {info && (
            <div className="mt-5 space-y-1 text-[15px] text-[#475569]" data-testid="order-finish-info">
              <p>Order: <b className="font-mono text-[#1A2536]">{info.number}</b></p>
              <p>Organization: <b className="text-[#1A2536]">{info.org_name}</b></p>
              <p>Codes: <b className="text-[#1A2536]">{info.quantity}</b> · {money(info.amount)} plus any applicable tax</p>
            </div>
          )}
          {done ? (
            <div className="mt-6 flex items-start gap-3 rounded-xl bg-[#DCFCE7] px-4 py-3 text-[15px] text-[#166534]" data-testid="order-finish-done">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              <span>Thank you! Your order is now an invoice order. Our team will review it, then email your codes and mail your invoice.</span>
            </div>
          ) : info && !info.open ? (
            <p className="mt-6 rounded-xl bg-[#F1F5F9] px-4 py-3 text-sm" data-testid="order-finish-closed">This order has already been completed. No further action is needed.</p>
          ) : info && (
            <div className="mt-6 space-y-3">
              <button className="btn-primary h-14 w-full text-lg" disabled={!!busy} onClick={card} data-testid="order-finish-card-button">
                {busy === "card" ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />} Pay by card now
              </button>
              <p className="text-center text-xs text-[#64748B]">Fastest: your codes are emailed as soon as payment goes through.</p>
              {!inv ? (
                <button className="btn-secondary h-12 w-full" onClick={() => setInv(true)} data-testid="order-finish-invoice-toggle"><FileText className="h-5 w-5" /> Switch to an invoice instead</button>
              ) : (
                <div className="space-y-3 rounded-xl border border-[#E2E8F0] p-4" data-testid="order-finish-invoice-form">
                  <label className="block"><span className="field-label">Mailing address for the invoice</span>
                    <textarea className="field-input h-auto py-3" rows={3} maxLength={300} value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="order-finish-address" /></label>
                  <p className="text-xs text-[#7C4A03]">Invoice orders take longer: our team approves the order before your codes are issued.</p>
                  <button className="btn-primary w-full" disabled={!!busy || addr.trim().length < 8} onClick={invoice} data-testid="order-finish-invoice-submit">
                    {busy === "inv" ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />} Send me an invoice
                  </button>
                </div>
              )}
            </div>
          )}
          {err && <div className="mt-4 flex items-start gap-3 rounded-xl bg-[#FEE2E2] px-4 py-3 text-[15px] text-[#991B1B]" data-testid="order-finish-error"><ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /> {err}</div>}
        </div>
      </div>
    </PublicShell>
  );
}
